import { ref, type Ref, type Component } from 'vue'

export type FeedbackToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface FeedbackToastAction {
  label: string
  onClick?: () => void
}

export interface FeedbackToast {
  id: string
  title: string
  description?: string
  variant?: FeedbackToastVariant
  icon?: Component
  duration?: number
  primaryAction?: FeedbackToastAction
  secondaryAction?: FeedbackToastAction
}

export type FeedbackToastInput = Omit<FeedbackToast, 'id'>

// Estado global compartilhado
const toasts: Ref<FeedbackToast[]> = ref([])
const defaultDuration = ref(5000)
const maxVisible = ref(3)

const generateId = () => `feedback-toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

const addToast = (toast: FeedbackToastInput): string => {
  const id = generateId()
  const newToast: FeedbackToast = { ...toast, id }
  toasts.value.push(newToast)

  const duration = toast.duration ?? defaultDuration.value
  if (duration > 0) {
    setTimeout(() => removeToast(id), duration)
  }

  return id
}

const removeToast = (id: string): void => {
  const index = toasts.value.findIndex((t) => t.id === id)
  if (index > -1) {
    toasts.value.splice(index, 1)
  }
}

const clearAll = (): void => {
  toasts.value = []
}

// Helper methods
const success = (title: string, options?: Partial<Omit<FeedbackToastInput, 'title' | 'variant'>>): string => {
  return addToast({ title, variant: 'success', ...options })
}

const error = (title: string, options?: Partial<Omit<FeedbackToastInput, 'title' | 'variant'>>): string => {
  return addToast({ title, variant: 'error', ...options })
}

const warning = (title: string, options?: Partial<Omit<FeedbackToastInput, 'title' | 'variant'>>): string => {
  return addToast({ title, variant: 'warning', ...options })
}

const info = (title: string, options?: Partial<Omit<FeedbackToastInput, 'title' | 'variant'>>): string => {
  return addToast({ title, variant: 'info', ...options })
}

export function useFeedbackToast() {
  return {
    // State
    toasts,
    defaultDuration,
    maxVisible,
    
    // Methods
    addToast,
    removeToast,
    clearAll,
    
    // Helpers
    success,
    error,
    warning,
    info,
  }
}

export default useFeedbackToast
